import React from 'react';
import Icon from '../../../components/AppIcon';

const EquipmentConnectivityPanel = ({ productionAreas }) => {
  const getAllEquipment = () => {
    const equipment = [];
    productionAreas?.forEach(area => {
      area?.equipment?.forEach(eq => {
        equipment?.push({
          ...eq,
          areaName: area?.name,
          areaStatus: area?.status
        });
      });
    });
    return equipment;
  };

  const allEquipment = getAllEquipment();
  const operationalCount = allEquipment?.filter(eq => eq?.operational)?.length;
  const offlineCount = allEquipment?.filter(eq => !eq?.operational)?.length;
  const totalCount = allEquipment?.length;
  const connectivityPercent = totalCount > 0 ? Math.round((operationalCount / totalCount) * 100) : 0;

  const getEquipmentIcon = (name) => {
    const lowerName = name?.toLowerCase() || '';
    if (lowerName?.includes('robot')) return 'Bot';
    if (lowerName?.includes('conveyor')) return 'ArrowRight';
    if (lowerName?.includes('scanner')) return 'Scan';
    if (lowerName?.includes('heater')) return 'Flame';
    if (lowerName?.includes('sensor')) return 'Radio';
    if (lowerName?.includes('cutter')) return 'Scissors';
    if (lowerName?.includes('press')) return 'Hammer';
    return 'Box';
  };

  return (
    <div className="bg-card rounded-xl border-2 border-border p-4 md:p-6">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
            <Icon name="Cpu" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h2 className="text-lg md:text-xl font-semibold text-foreground">
              Equipment Connectivity
            </h2>
            <p className="text-sm text-muted-foreground">
              Real-time equipment status monitoring
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-success/10 border border-success">
          <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
          <span className="text-xs font-medium text-success">Live</span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mb-4 md:mb-6">
        <div className="bg-background rounded-lg border-2 border-border p-3 md:p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Activity" size={16} color="var(--color-muted-foreground)" />
            <span className="text-xs text-muted-foreground">Total Equipment</span>
          </div>
          <p className="text-2xl md:text-3xl font-bold text-foreground">{totalCount}</p>
        </div>
        <div className="bg-background rounded-lg border-2 border-success p-3 md:p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="CheckCircle2" size={16} color="var(--color-success)" />
            <span className="text-xs text-muted-foreground">Operational</span>
          </div>
          <p className="text-2xl md:text-3xl font-bold text-success">{operationalCount}</p>
        </div>
        <div className="bg-background rounded-lg border-2 border-error p-3 md:p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="XCircle" size={16} color="var(--color-error)" />
            <span className="text-xs text-muted-foreground">Offline</span>
          </div>
          <p className="text-2xl md:text-3xl font-bold text-error">{offlineCount}</p>
        </div>
      </div>

      <div className="mb-4 md:mb-6">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-muted-foreground">Overall Connectivity</span>
          <span className="font-semibold text-foreground">{connectivityPercent}%</span>
        </div>
        <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${
              connectivityPercent >= 90
                ? 'bg-success'
                : connectivityPercent >= 70
                ? 'bg-warning' :'bg-error'
            }`}
            style={{ width: `${connectivityPercent}%` }}
          />
        </div>
      </div>

      <div className="space-y-2">
        {allEquipment?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Icon name="Cpu" size={48} color="var(--color-muted-foreground)" />
            <p className="text-sm text-muted-foreground mt-3">
              No equipment found
            </p>
          </div>
        ) : (
          allEquipment?.map((equipment) => (
            <div
              key={equipment?.id}
              className={`flex items-center justify-between gap-3 p-3 rounded-lg border-2 transition-all duration-200 ${
                equipment?.operational
                  ? 'bg-success/5 border-success/20 hover:border-success' :'bg-error/5 border-error/20 hover:border-error'
              }`}
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className={`flex items-center justify-center w-9 h-9 rounded-lg ${
                  equipment?.operational ? 'bg-success/10' : 'bg-error/10'
                }`}>
                  <Icon
                    name={getEquipmentIcon(equipment?.name)}
                    size={18}
                    color={equipment?.operational ? 'var(--color-success)' : 'var(--color-error)'}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate">
                    {equipment?.name}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {equipment?.areaName}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {equipment?.operational ? (
                  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-success/10 border border-success">
                    <div className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
                    <span className="text-xs font-medium text-success">Online</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-error/10 border border-error">
                    <div className="w-1.5 h-1.5 rounded-full bg-error" />
                    <span className="text-xs font-medium text-error">Offline</span>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default EquipmentConnectivityPanel;